import {PropsWithChildren, ReactNode} from "react";

export type OtsBadgeTone = "neutral" | "info" | "success" | "warning" | "danger" | "blue" | "yellow" | "violet" | "indigo";
export type OtsBadgeSize = "compact" | "default";

export type OtsBadgeProps = PropsWithChildren<{
  tone?: OtsBadgeTone;
  size?: OtsBadgeSize;
  icon?: ReactNode;
  dot?: boolean;
  ariaLabel?: string;
  className?: string;
}>;

const toneClasses: Record<OtsBadgeTone, string> = {
  neutral: "border-[var(--ots-outline-variant)] bg-[var(--ots-neutral-container)] text-[var(--ots-on-neutral-container)]",
  info: "border-[var(--ots-outline)] bg-[var(--ots-primary-container)] text-[var(--ots-primary)]",
  success: "border-[var(--ots-success-outline)] bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)]",
  warning: "border-[var(--ots-warning-outline)] bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]",
  danger: "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]",
  blue: "border-[var(--ots-blue-outline)] bg-[var(--ots-blue-container)] text-[var(--ots-on-blue-container)]",
  yellow: "border-[var(--ots-yellow-outline)] bg-[var(--ots-yellow-container)] text-[var(--ots-on-yellow-container)]",
  violet: "border-[var(--ots-violet-outline)] bg-[var(--ots-violet-container)] text-[var(--ots-on-violet-container)]",
  indigo: "border-[var(--ots-indigo-outline)] bg-[var(--ots-indigo-container)] text-[var(--ots-on-indigo-container)]",
};

/** Foundational theme-aware badge for types, statuses, categories, and compact metadata. */
export const OtsBadge = ({
  tone = "neutral",
  size = "default",
  icon,
  dot = false,
  ariaLabel,
  className,
  children,
}: OtsBadgeProps) => <span
  aria-label={ariaLabel}
  data-tone={tone}
  data-size={size}
  className={[
    "inline-flex items-center rounded-full border font-semibold",
    size === "compact" ? "gap-1 px-2 py-0.5 text-[11px]" : "gap-1.5 px-2.5 py-1 text-xs",
    toneClasses[tone],
    className,
  ].filter(Boolean).join(" ")}>
  {dot && <span aria-hidden="true" className="size-1.5 rounded-full bg-current"/>}
  {icon != null && <span aria-hidden="true" className="size-3.5 shrink-0 [&>svg]:size-full">{icon}</span>}
  {children}
</span>;
